import React, { useState, useEffect, useContext } from 'react';
import { IoCash, IoReceiptOutline, IoFilter } from 'react-icons/io5';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import * as XLSX from 'xlsx';

const CompanyDashboard = () => {
  const { user, token } = useContext(AuthContext);
  const API = process.env.REACT_APP_BACKEND_URL;

  const [companyName, setCompanyName] = useState('');
  const [startingBalance, setStartingBalance] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [allTransactions, setAllTransactions] = useState([]); // simplified rows for table
  const [rawOrders, setRawOrders] = useState([]);             // full orders for export
  const [rawTopups, setRawTopups] = useState([]);             // full topups for export
  const [view, setView] = useState('transactions'); // "transactions" | "topups"
  const [filter, setFilter] = useState('all'); // "all" | "day" | "week" | "month" | "range"
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [firstTopupDate, setFirstTopupDate] = useState(null);

  // Fetch company details & first top-up date
  useEffect(() => {
    if (!user?.companyId) return;

    const fetchCompanyData = async () => {
      try {
        const { data: details } = await axios.get(
          `${API}/api/company/details/${user.companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCompanyName(details.companyName || '');
        setStartingBalance(details.startingBalance || 0);
        setCurrentBalance(details.currentBalance || 0);

        const { data: loads } = await axios.get(
          `${API}/api/load-amount`,
          { headers: { Authorization: `Bearer ${token}` }, params: { companyId: user.companyId } }
        );

        // keep a copy of all topups; useful later if user switches to "Top-ups" and exports immediately
        setRawTopups(loads || []);

        const companyLoads = (loads || []).filter(l => l.companyId === user.companyId);
        const earliest = companyLoads.length > 0
          ? new Date(Math.min(...companyLoads.map(l => new Date(l.dateOfTransaction))))
          : null;

        setFirstTopupDate(earliest || new Date(0));
      } catch (err) {
        console.error('Error fetching company data:', err);
      }
    };

    fetchCompanyData();
  }, [API, user?.companyId, token]);

  // Fetch all transactions whenever `view` or `firstTopupDate` changes
  useEffect(() => {
    if (!user?.companyId || !firstTopupDate) return;

    const fetchAllTransactions = async () => {
      try {
        if (view === 'transactions') {
          // Orders since first topup date, for this company
          const params = {
            companyId: user.companyId,
            ORDER_DATE: { $gte: firstTopupDate.toISOString().split('T')[0] },
            TRANSACTION_TYPE: { $in: ['POINTS', 'POINTS+CASH'] }
          };
          const { data: orders } = await axios.get(`${API}/api/orders`, {
            headers: { Authorization: `Bearer ${token}` },
            params
          });

          setRawOrders(orders || []);
          setAllTransactions(
            (orders || []).map(tx => ({
              ORDER_ID: tx.ORDER_ID,
              ORDER_DATE: tx.ORDER_DATE,
              POINTS_PAYMENT_AMOUNT: tx.POINTS_PAYMENT_AMOUNT || 0,
              type: 'purchase',
              description: `Order #${tx.ORDER_ID}`
            }))
          );
        } else {
          const { data: loads } = await axios.get(`${API}/api/load-amount`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { companyId: user.companyId }
          });

          setRawTopups(loads || []);
          setAllTransactions(
            (loads || []).map(tx => ({
              id: tx.id,
              date: tx.dateOfTransaction,
              amount: tx.equivalentCoins,
              type: 'topup',
              description: `Top-up ${tx.invoiceNumber}`
            }))
          );
        }
      } catch (err) {
        console.error('Error fetching transactions:', err);
      }
    };

    fetchAllTransactions();
  }, [view, firstTopupDate, API, token, user?.companyId]);

  const filterTransactions = (transactions) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    if (filter === 'day') {
      return transactions.filter(tx => {
        const txDate = new Date(tx.ORDER_DATE || tx.date);
        const endToday = new Date(today);
        endToday.setHours(23, 59, 59, 999);
        return txDate >= today && txDate <= endToday;
      });
    }
    if (filter === 'week') {
      return transactions.filter(tx => new Date(tx.ORDER_DATE || tx.date) >= weekAgo);
    }
    if (filter === 'month') {
      return transactions.filter(tx => new Date(tx.ORDER_DATE || tx.date) >= monthStart);
    }
    if (filter === 'range' && dateRange.from && dateRange.to) {
      const start = new Date(dateRange.from);
      const end = new Date(dateRange.to);
      end.setHours(23, 59, 59, 999);
      return transactions.filter(tx => {
        const txDate = new Date(tx.ORDER_DATE || tx.date);
        return txDate >= start && txDate <= end;
      });
    }
    return transactions;
  };

  // --------- Excel export helpers ---------
  const flattenObject = (obj, prefix = '', out = {}) => {
    if (obj === null || obj === undefined) return out;
    Object.entries(obj).forEach(([k, v]) => {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)) {
        flattenObject(v, key, out);
      } else {
        out[key] = Array.isArray(v) ? JSON.stringify(v) : v;
      }
    });
    return out;
  };

  const exportToExcel = (rows, sheetName, filename) => {
    const flat = rows.map(r => flattenObject(r));
    const ws = XLSX.utils.json_to_sheet(flat);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, filename);
  };

  const handleExportDetailed = () => {
    // rows visible in the table after filters
    const displayed = filterTransactions(allTransactions);

    if (view === 'transactions') {
      const idSet = new Set(displayed.map(r => r.ORDER_ID));
      const rowsToExport = (rawOrders || []).filter(o => idSet.has(o.ORDER_ID));
      exportToExcel(
        rowsToExport,
        'Transactions',
        `transactions_${(companyName || 'company').replace(/\s+/g, '-')}_${new Date().toISOString().slice(0,10)}.xlsx`
      );
    } else {
      const idSet = new Set(displayed.map(r => r.id));
      const rowsToExport = (rawTopups || []).filter(t => idSet.has(t.id));
      exportToExcel(
        rowsToExport,
        'Topups',
        `topups_${(companyName || 'company').replace(/\s+/g, '-')}_${new Date().toISOString().slice(0,10)}.xlsx`
      );
    }
  };

  if (!user?.companyId) {
    return (
      <div className="flex-1 ml-64 p-6 text-gray-700">
        No company assigned. Please contact your administrator.
      </div>
    );
  }

  return (
    <div className="flex-1 ml-64 p-6 bg-white text-gray-800 overflow-auto">
      {/* Header */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b pb-4">
        <div>
          <h1 className="text-3xl font-semibold">{companyName}</h1>
          <p className="text-sm text-gray-600 mt-1">
            Starting Balance:{' '}
            <span className="font-bold text-green-600">₹{startingBalance.toFixed(2)}</span>
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Current Balance:{' '}
            <span className="font-bold text-green-600">₹{currentBalance.toFixed(2)}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setView('transactions')}
            className={`flex items-center px-4 py-2 rounded transition ${
              view === 'transactions'
                ? 'bg-teal-600 text-white shadow'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <IoCash className="mr-1" /> Transactions
          </button>
          <button
            onClick={() => setView('topups')}
            className={`flex items-center px-4 py-2 rounded transition ${
              view === 'topups'
                ? 'bg-teal-600 text-white shadow'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <IoReceiptOutline className="mr-1" /> Top-ups
          </button>
          <button
            onClick={handleExportDetailed}
            className="flex items-center px-4 py-2 rounded transition bg-indigo-600 text-white hover:bg-indigo-700"
            title="Exports all details of the rows currently visible after filters"
          >
            Export to Excel
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <IoFilter className="text-gray-500" />
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="p-2 border rounded"
        >
          <option value="all">All time</option>
          <option value="day">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="range">Date Range</option>
        </select>
        {filter === 'range' && (
          <>
            <input
              type="date"
              name="from"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="p-2 border rounded"
              min={firstTopupDate ? firstTopupDate.toISOString().split('T')[0] : undefined}
            />
            <input
              type="date"
              name="to"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="p-2 border rounded"
            />
          </>
        )}
      </div>

      {/* Data Table */}
      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="w-full text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">Description</th>
              <th className="p-3">Amount (₹)</th>
              <th className="p-3">Type</th>
            </tr>
          </thead>
          <tbody>
            {filterTransactions(allTransactions).map(tx => (
              <tr key={tx.ORDER_ID || tx.id || tx.date} className="border-t hover:bg-gray-50">
                <td className="p-3">{new Date(tx.ORDER_DATE || tx.date).toLocaleString('en-GB')}</td>
                <td className="p-3">{tx.description}</td>
                <td className="p-3">{Number(tx.POINTS_PAYMENT_AMOUNT || tx.amount || 0).toFixed(2)}</td>
                <td className="p-3 capitalize">{tx.type}</td>
              </tr>
            ))}
            {filterTransactions(allTransactions).length === 0 && (
              <tr>
                <td colSpan="4" className="p-4 text-center text-gray-500">
                  No {view === 'topups' ? 'top-ups' : 'transactions'} found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompanyDashboard;
